import { Lock, Key, Link } from 'lucide-react'
import SecureInput from '../../../ui/SecureInput/SecureInput'
import { CreateIdpProviderDto } from '../../../types/types'
import styles from '../SSOConfiguration.module.css'

interface IasProviderFormProps {
  providerForm: CreateIdpProviderDto
  onFieldChange: (field: keyof CreateIdpProviderDto, value: string) => void
  onConfigChange: (key: string, value: string) => void
  hasClientSecret?: boolean
}

export default function IasProviderForm({
  providerForm,
  onFieldChange,
  onConfigChange,
  hasClientSecret
}: IasProviderFormProps) {
  return (
    <div className={styles.providerFormGrid}>
      <div className={styles.formField}>
        <SecureInput
          fieldType="name"
          label="Name *"
          value={providerForm.name}
          onChange={(value) => onFieldChange('name', value)}
          placeholder="e.g. SAP IAS Production"
          className={styles.input}
        />
      </div>

      <div className={styles.formField}>
        <SecureInput
          fieldType="text"
          label="Description"
          value={providerForm.description || ''}
          onChange={(value) => onFieldChange('description', value)}
          placeholder="Optional description"
          className={styles.input}
        />
      </div>

      <div className={styles.formField}>
        <SecureInput
          fieldType="key"
          label="Client ID *"
          icon={<Key size={16} />}
          value={providerForm.clientId || ''}
          onChange={(value) => onFieldChange('clientId', value)}
          placeholder="SAP IAS Application Client ID"
          className={styles.input}
          description="OAuth2 Client ID from SAP IAS Application"
        />
      </div>

      <div className={styles.formField}>
        <SecureInput
          fieldType="password"
          label="Client Secret *"
          icon={<Lock size={16} />}
          value={providerForm.clientSecret || ''}
          onChange={(value) => onFieldChange('clientSecret', value)}
          placeholder={hasClientSecret ? 'Secret is set — leave blank to keep it' : 'SAP IAS Application Client Secret'}
          className={styles.input}
          skipPasswordValidation
          description={hasClientSecret
            ? 'A secret is already configured. Leave blank to keep it, or enter a new value to replace it.'
            : 'OAuth2 Client Secret from SAP IAS Application'}
          autoComplete="new-password"
        />
      </div>

      <div className={styles.formField}>
        <SecureInput
          fieldType="url"
          label="Issuer URL"
          icon={<Link size={16} />}
          value={providerForm.issuerUrl || ''}
          onChange={(value) => onFieldChange('issuerUrl', value)}
          placeholder="https://your-tenant.accounts.ondemand.com"
          className={styles.input}
          description="SAP IAS Tenant URL"
        />
      </div>

      <div className={styles.formField}>
        <SecureInput
          fieldType="text"
          label="Authorization URL"
          value={providerForm.authorizationUrl || ''}
          onChange={(value) => onFieldChange('authorizationUrl', value)}
          placeholder="/oauth2/authorize"
          className={styles.input}
          description="SAP IAS Authorization URL"
        />
      </div>

      <div className={styles.formField}>
        <SecureInput
          fieldType="text"
          label="Token URL"
          value={providerForm.tokenUrl || ''}
          onChange={(value) => onFieldChange('tokenUrl', value)}
          placeholder="/oauth2/token"
          className={styles.input}
          description="SAP IAS Token URL"
        />
      </div>

      <div className={styles.formField}>
        <SecureInput
          fieldType="text"
          label="Scopes"
          value={providerForm.config?.scope || ''}
          onChange={(value) => onConfigChange('scope', value)}
          placeholder="openid email profile"
          className={styles.input}
          description="OAuth2 scopes to request (space-separated)"
          autoComplete="off"
        />
      </div>
    </div>
  )
}
